import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import { MaskedLines, Reveal, FadeIn } from "@/components/Motion";
import { Chapter, CtaBanner, MarqueeStrip, QuoteButtons } from "@/components/Sections";
import { B2B_CATEGORIES, PROCESS_STEPS, REASONS, SERVICES } from "@/lib/content";

const MARQUEE_ITEMS = [
  "Malerentreprise",
  "Kun erhverv",
  "B2B",
  "Underentreprise",
  "Erhvervsmaling",
  "Nybyggeri & renovering",
  "Koordinering med øvrige fag",
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-28">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-end pb-10">
        <div className="lg:col-span-7 relative z-10">
          <FadeIn delay={0.1}>
            <div data-testid="hero-b2b-badge" className="inline-flex items-center gap-3 border border-ink/20 px-4 py-2 mb-8">
              <span className="w-2 h-2 bg-signal rotate-45" />
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold">Kun erhverv · B2B</span>
            </div>
          </FadeIn>
          <h1 className="font-cabinet font-extrabold tracking-tighter text-[13vw] sm:text-7xl lg:text-[5.6rem] leading-[0.95]">
            <MaskedLines
              lines={[
                <>Professionelt</>,
                <>malerarbejde</>,
                <>til <span className="font-editorial italic font-medium text-signal">erhverv</span></>,
              ]}
            />
          </h1>
          <FadeIn delay={0.7}>
            <p data-testid="hero-subheadline" className="mt-8 text-base md:text-lg text-soot leading-relaxed max-w-xl">
              Al Maler Mester ApS udfører professionelle maleropgaver for virksomheder, entreprenører og
              erhvervskunder i Danmark. Vi håndterer både mindre opgaver og større malerentrepriser med fokus
              på kvalitet, fleksibilitet og overholdelse af aftaler.
            </p>
            <QuoteButtons className="mt-10" />
          </FadeIn>
        </div>
        <div className="lg:col-span-5 relative">
          <motion.div style={{ y }} className="relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="grain overflow-hidden"
            >
              <motion.img
                src="/images/hero.jpg"
                alt="Professionel maler på stillads ved moderne erhvervsejendom"
                data-testid="hero-image"
                style={{ scale }}
                className="w-full h-[380px] lg:h-[560px] object-cover"
              />
            </motion.div>
            <FadeIn delay={1}>
              <div className="absolute -bottom-5 -left-5 bg-bone border border-ink/10 px-5 py-4 backdrop-blur-xl bg-bone/90">
                <p className="font-cabinet font-bold text-sm">Malerentreprenør</p>
                <p className="text-xs text-soot mt-0.5">Hoved- og underentreprise · Hele Danmark</p>
              </div>
            </FadeIn>
          </motion.div>
        </div>
      </div>
      <FadeIn delay={1.1} className="mt-10">
        <MarqueeStrip items={MARQUEE_ITEMS} />
      </FadeIn>
    </section>
  );
};

const Intro = () => (
  <section data-testid="intro-section" className="max-w-[1440px] mx-auto px-5 md:px-10 py-24 md:py-36">
    <Chapter nr="01" label="Samarbejdspartner" />
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-7">
        <h2 className="font-cabinet text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-balance">
          Din professionelle samarbejdspartner inden for{" "}
          <span className="font-editorial italic font-medium text-signal">malerarbejde</span>
        </h2>
      </Reveal>
      <Reveal delay={0.12} className="lg:col-span-5">
        <p className="text-base text-soot leading-relaxed">
          Al Maler Mester ApS leverer professionelle malerløsninger til danske virksomheder. Vi kan arbejde
          direkte for jeres virksomhed – eller indgå som underentreprenør på større bygge- og
          renoveringsprojekter, hvor vi koordinerer tæt med hovedentreprenør og øvrige faggrupper.
        </p>
        <p className="mt-4 text-base text-soot leading-relaxed">
          Vi påtager os både små og store opgaver og tilpasser kapacitet og bemanding efter projektets omfang
          og tidsplan.
        </p>
      </Reveal>
    </div>
    <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-ink/10">
      {[
        "B2B-specialisering",
        "Professionel udførelse",
        "Fleksibel kapacitet",
        "Koordinering med andre fag",
        "Fokus på deadlines",
        "Klar kommunikation",
      ].map((item, i) => (
        <Reveal key={item} delay={i * 0.06} y={16}>
          <div className="px-5 py-6 border-ink/10 border-b md:border-b-0 md:border-r last:border-r-0 h-full" data-testid={`intro-point-${i}`}>
            <p className="text-xs uppercase tracking-[0.15em] font-semibold leading-relaxed">{item}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const ServicesGrid = () => (
  <section data-testid="services-section" className="py-24 md:py-36 bg-white grain">
    <div className="max-w-[1440px] mx-auto px-5 md:px-10">
      <Chapter nr="02" label="Ydelser" />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <Reveal>
          <h2 className="font-cabinet text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-balance max-w-2xl">
            Malerydelser til erhverv og byggeprojekter
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/ydelser"
            data-testid="services-all-link"
            className="group flex items-center gap-2 text-sm font-semibold border-b border-ink pb-1 hover:text-signal hover:border-signal transition-colors duration-300"
          >
            Se alle ydelser
            <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-ink/10">
        {SERVICES.map((s, i) => (
          <Reveal key={s.nr} delay={(i % 3) * 0.08} y={20}>
            <Link
              to="/ydelser"
              data-testid={`service-card-${s.nr}`}
              className="group block h-full border-b border-r border-ink/10 p-8 md:p-10 hover:bg-ink transition-colors duration-500"
            >
              <div className="flex items-start justify-between">
                <span className="font-editorial italic text-lg text-signal">({s.nr})</span>
                <ArrowUpRight
                  size={18}
                  weight="bold"
                  className="text-soot group-hover:text-signal transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </div>
              <h3 className="mt-10 font-cabinet text-xl font-bold tracking-tight group-hover:text-bone transition-colors duration-500">
                {s.title}
              </h3>
              <p className="mt-3 text-sm text-soot leading-relaxed group-hover:text-bone/60 transition-colors duration-500">
                {s.desc}
              </p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const B2BSection = () => (
  <section data-testid="b2b-section" className="bg-ink text-bone grain py-24 md:py-36">
    <div className="max-w-[1440px] mx-auto px-5 md:px-10">
      <Chapter nr="03" label="Kun erhverv" dark />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-7">
          <h2 className="font-cabinet text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-balance">
            Malerfirma for erhverv og professionelle{" "}
            <span className="font-editorial italic font-medium text-signal">samarbejdspartnere</span>
          </h2>
          <p className="mt-8 text-bone/60 text-base md:text-lg leading-relaxed max-w-xl">
            Al Maler Mester ApS arbejder udelukkende med erhvervskunder. Det gør os til en oplagt partner for
            virksomheder, der har brug for en fast malerentreprenør – eller en fleksibel underentreprenør til
            det enkelte projekt.
          </p>
          <p className="mt-4 text-bone/60 text-base leading-relaxed max-w-xl">
            Har jeres virksomhed brug for en maler eller underentreprenør? Kontakt os for en dialog om opgaven.
          </p>
          <QuoteButtons className="mt-10" />
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <ul className="border-t border-bone/15">
            {B2B_CATEGORIES.map((c, i) => (
              <li
                key={c}
                data-testid={`b2b-category-${i}`}
                className="flex items-center justify-between py-4 border-b border-bone/15 group"
              >
                <span className="font-cabinet font-medium tracking-tight group-hover:text-signal transition-colors duration-300">
                  {c}
                </span>
                <span className="font-editorial italic text-signal text-sm">0{i + 1}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </div>
  </section>
);

const Reasons = () => (
  <section data-testid="reasons-section" className="py-24 md:py-36 max-w-[1440px] mx-auto px-5 md:px-10">
    <Chapter nr="04" label="Derfor os" />
    <Reveal>
      <h2 className="font-cabinet text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-balance max-w-3xl mb-14">
        Derfor vælger virksomheder Al Maler Mester ApS
      </h2>
    </Reveal>
    <div className="grid grid-cols-1 md:grid-cols-6 gap-px bg-ink/10 border border-ink/10">
      {REASONS.map((r, i) => (
        <Reveal
          key={r.nr}
          delay={(i % 3) * 0.08}
          y={20}
          className={i < 2 ? "md:col-span-3" : "md:col-span-2"}
        >
          <div data-testid={`reason-card-${r.nr}`} className="bg-bone p-8 md:p-10 h-full hover:bg-white transition-colors duration-500">
            <span className="font-editorial italic text-2xl text-signal">{r.nr}</span>
            <h3 className="mt-6 font-cabinet text-xl font-bold tracking-tight">{r.title}</h3>
            <p className="mt-3 text-sm text-soot leading-relaxed">{r.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Process = () => (
  <section data-testid="process-section" className="py-24 md:py-36 bg-white grain">
    <div className="max-w-[1440px] mx-auto px-5 md:px-10">
      <Chapter nr="05" label="Sådan arbejder vi" />
      <Reveal>
        <h2 className="font-cabinet text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-14">
          Fra henvendelse til færdigt resultat
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">
        {PROCESS_STEPS.map((s, i) => (
          <Reveal key={s.nr} delay={i * 0.1} y={24}>
            <div data-testid={`process-step-${s.nr}`} className="relative pt-8 border-t-2 border-ink">
              <span className="absolute -top-[9px] left-0 w-4 h-4 bg-signal" />
              <span className="font-editorial italic text-4xl text-ink/20">{s.nr}.</span>
              <h3 className="mt-3 font-cabinet text-lg font-bold tracking-tight">{s.title}</h3>
              <p className="mt-2 text-sm text-soot leading-relaxed">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ProjectsTeaser = () => (
  <section data-testid="projects-teaser" className="py-24 md:py-36 max-w-[1440px] mx-auto px-5 md:px-10">
    <Chapter nr="06" label="Projekter" />
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
      <Reveal>
        <h2 className="font-cabinet text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
          Udvalgte projekter
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Link
          to="/projekter"
          data-testid="projects-all-link"
          className="group flex items-center gap-2 text-sm font-semibold border-b border-ink pb-1 hover:text-signal hover:border-signal transition-colors duration-300"
        >
          Se projekter
          <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {["/images/facade.jpg", "/images/office.jpg", "/images/warehouse.jpg"].map((img, i) => (
        <Reveal key={img} delay={i * 0.1}>
          <Link to="/projekter" className="group block grain overflow-hidden relative" data-testid={`project-teaser-${i}`}>
            <img
              src={img}
              alt="Projektdokumentation på vej"
              className="w-full h-[340px] object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.03] transition-[filter,transform] duration-700"
            />
            <div className="absolute inset-0 bg-ink/30 group-hover:bg-ink/0 transition-colors duration-700" />
            <div className="absolute bottom-4 left-4 bg-bone/90 backdrop-blur-xl px-4 py-2.5">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-signal">Placeholder</p>
              <p className="text-xs font-medium mt-0.5">Projektdokumentation på vej</p>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const Home = () => (
  <>
    <Seo
      title="Al Maler Mester ApS — Professionelt malerarbejde til erhverv | Malerfirma Rødovre & København"
      description="Professionelt B2B-malerfirma i Rødovre. Vi udfører erhvervsmaling, malerentrepriser og underentreprise for virksomheder, entreprenører og ejendomsselskaber i hele Danmark. Indhent tilbud i dag."
    />
    <Hero />
    <Intro />
    <ServicesGrid />
    <B2BSection />
    <Reasons />
    <Process />
    <ProjectsTeaser />
    <CtaBanner />
  </>
);

export default Home;
